import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const navigation = [
  { name: "Domov", href: "/" },
  { name: "O nas", href: "/o-nas" },
  { name: "Projekti", href: "/projekti" },
  { name: "Kuhinje", href: "/kuhinje" },
  { name: "Tehnologija", href: "/tehnologija" },
  { name: "Produkti", href: "/produkti" },
  { name: "Projektiranje", href: "/projektiranje" },
  { name: "Kontakt", href: "/kontakt" },
];

export function Navbar({ solid = false }: { solid?: boolean } = {}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const usesLightHeader = location.pathname === "/kalkulator" || location.pathname === "/cenik";
  const showSolidHeader = solid || isScrolled || usesLightHeader;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        showSolidHeader
          ? "bg-background/95 backdrop-blur-md shadow-md"
          : "bg-transparent"
      )}
    >
      <div className="container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className={cn(
              "text-2xl font-bold tracking-tight transition-colors",
              showSolidHeader ? "text-foreground" : "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]"
            )}
          >
            Interoform
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "text-sm font-medium transition-colors",
                  showSolidHeader 
                    ? (location.pathname === item.href ? "text-foreground hover:text-accent" : "text-muted-foreground hover:text-accent")
                    : (location.pathname === item.href ? "text-white hover:text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" : "text-white/80 hover:text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]")
                )}
              >
                {item.name}
              </Link>
            ))}
            <Button asChild variant="default" className="ml-4">
              <Link to="/kontakt">Povpraševanje</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={cn(
              "lg:hidden p-2 transition-colors",
              showSolidHeader ? "text-foreground" : "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]"
            )}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/20 bg-black/60 backdrop-blur-xl -mx-4 px-4 rounded-b-xl">
            <div className="flex flex-col gap-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "text-base font-medium transition-colors hover:text-accent",
                    location.pathname === item.href
                      ? "text-white"
                      : "text-white/70"
                  )}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <Button asChild variant="default" className="mt-2 w-full">
                <Link to="/kontakt" onClick={() => setIsMobileMenuOpen(false)}>
                  Povpraševanje
                </Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
